import React, { useCallback, useEffect, useState } from 'react';
import { Check, X, Send, RotateCcw } from 'lucide-react';
import Modal from './Modal';
import Button from './Button';
import Skeleton from './Skeleton';
import Toast, { ToastMessage } from './Toast';
import workflowService, { WfTimeline, WfAction } from '../../services/workflowService';
import { useFieldValidation } from '../../validation/useFieldValidation';
import { ValidatedTextarea } from '../../validation/ValidatedFields';
import { useEntitySettings } from '../../contexts/EntitySettingsContext';

interface WorkflowModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** resource_kind in the backend WF registry, e.g. 'rsk_register'. */
  kind: string | null;
  id: number | null;
  /** Called after any successful transition so the parent can refresh. */
  onChanged?: () => void;
}

const ACTION_META: Record<WfAction, { color: string; label: string }> = {
  created:   { color: '#9ca3af', label: 'Created' },
  submitted: { color: '#eab308', label: 'Submitted' },
  approved:  { color: '#22c55e', label: 'Approved' },
  rejected:  { color: '#ef4444', label: 'Rejected' },
  reopened:  { color: '#a855f7', label: 'Reopened' },
  updated:   { color: '#9ca3af', label: 'Updated' },
};

/**
 * Common POLYMORPHIC maker-checker modal. Self-fetches the timeline for
 * {kind, id} from /api/v1/wf, renders a vertical dotted timeline (latest first:
 * action + person + date + note), and surfaces the next action (Submit /
 * Approve / Reject) at the top, gated by the engine's permissions for the
 * current user + state. One modal + one API for every module.
 */
const WorkflowModal: React.FC<WorkflowModalProps> = ({ isOpen, onClose, kind, id, onChanged }) => {
  const [tl, setTl] = useState<WfTimeline | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState<'idle' | 'approve' | 'reject'>('idle');
  const [note, setNote] = useState('');
  const [reason, setReason] = useState('');
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const vh = useFieldValidation();
  const { formatDateTime } = useEntitySettings();

  const load = useCallback(async () => {
    if (!kind || !id) return;
    setLoading(true);
    try {
      setTl(await workflowService.timeline(kind, id));
    } catch (e: any) {
      setToast({ type: 'error', message: e?.response?.data?.detail || 'Failed to load workflow' });
    } finally {
      setLoading(false);
    }
  }, [kind, id]);

  useEffect(() => {
    if (isOpen) { setMode('idle'); setNote(''); setReason(''); setToast(null); vh.reset(); load(); }
    // `vh` is intentionally omitted: useFieldValidation returns a fresh object
    // every render, so including it would re-run this effect on every render
    // (each run calls load() → setTl → re-render → new vh → loop). vh.reset is
    // a stable useCallback, so calling it here is safe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, load]);

  const run = async (fn: () => Promise<WfTimeline>) => {
    setBusy(true);
    try {
      setTl(await fn());
      setMode('idle'); setNote(''); setReason(''); vh.reset();
      onChanged?.();
    } catch (e: any) {
      setToast({ type: 'error', message: e?.response?.data?.detail || 'Action failed' });
    } finally {
      setBusy(false);
    }
  };

  const state = tl?.workflow_state ?? 'draft';
  const perms = tl?.permissions ?? { submit: false, approve: false, reject: false, reopen: false };
  const stateColor = ACTION_META[(state === 'draft' ? 'created' : state) as WfAction]?.color ?? '#9ca3af';

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <Modal.Header>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <span style={{ width: 12, height: 12, borderRadius: '50%', background: stateColor, boxShadow: `0 0 0 3px ${stateColor}22` }} />
          Approval Workflow
        </span>
      </Modal.Header>
      <Modal.Body>
        <div style={{ maxHeight: '60vh', overflowY: 'auto' }} className="flex flex-col gap-y-4">
          {tl?.title && (
            <div className="item-highlight" style={{ fontSize: 15, fontWeight: 600, wordBreak: 'break-word' }}>
              {tl.title}
            </div>
          )}

          {/* ── Loading skeleton — mirrors the real layout (title · state card · timeline) ── */}
          {loading && (
            <div className="flex flex-col gap-y-4" aria-busy="true" aria-live="polite">
              <Skeleton.Box width="55%" height={16} />
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <Skeleton.Box width={72} height={12} />
                  <Skeleton.Box width={56} height={12} />
                </div>
                <Skeleton.Pill width={120} height={30} />
              </div>
              <div className="flex flex-col">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '20px 1fr', columnGap: 12 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <Skeleton.Circle size={12} style={{ marginTop: 4 }} />
                      {i < 2 && <span style={{ flex: 1, width: 2, background: '#e5e7eb', marginTop: 2, minHeight: 24 }} />}
                    </div>
                    <div style={{ paddingBottom: 16, display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <Skeleton.Box width="45%" height={13} />
                      <Skeleton.Box width="30%" height={10} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Current action (top) ─────────────────────────────────── */}
          {!loading && tl && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 flex flex-col gap-y-2">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Current State</span>
                  <span className="text-sm font-semibold capitalize" style={{ color: stateColor }}>{state}</span>
                </div>

                {mode === 'idle' && (
                  <div className="flex gap-2">
                    {state === 'draft' && perms.submit && (
                      <Button variant="fill" icon={Send} disabled={busy}
                        onClick={() => run(() => workflowService.submit(kind!, id!))}
                        data-testid="BTN_wf_modal_submit">
                        Submit for Approval
                      </Button>
                    )}

                    {state === 'submitted' && perms.reject && (
                      <Button variant="inactive-outline" icon={X} disabled={busy}
                        onClick={() => setMode('reject')} data-testid="BTN_wf_modal_reject_open">Reject</Button>
                    )}
                    {state === 'submitted' && perms.approve && (
                      <Button variant="success" icon={Check} disabled={busy}
                        onClick={() => setMode('approve')} data-testid="BTN_wf_modal_approve_open">Approve</Button>
                    )}

                    {(state === 'approved' || state === 'rejected') && perms.reopen && (
                      <Button variant="inactive-outline" icon={RotateCcw} disabled={busy}
                        onClick={() => run(() => workflowService.reopen(kind!, id!))}
                        data-testid="BTN_wf_modal_reopen">
                        Reopen to Draft
                      </Button>
                    )}
                  </div>
                )}
              </div>

              {mode === 'approve' && (
                <div className="flex flex-col gap-y-2">
                  <ValidatedTextarea vh={vh} name="note" code={110} label="Approval note (optional)" value={note}
                    onChange={(e) => setNote(e.target.value)} rows={2}
                    placeholder="Optional comment for the audit trail…" data-testid="INP_wf_modal_note" />
                  <div className="flex gap-2 justify-end">
                    <Button variant="inactive-outline" disabled={busy} onClick={() => setMode('idle')}>Cancel</Button>
                    <Button variant="success" icon={Check} disabled={busy}
                      onClick={() => {
                        if (!vh.validateAll([{ name: 'note', code: 110, value: note }])) return;
                        run(() => workflowService.approve(kind!, id!, note.trim() || undefined));
                      }}
                      data-testid="BTN_wf_modal_approve_confirm">Confirm Approve</Button>
                  </div>
                </div>
              )}

              {mode === 'reject' && (
                <div className="flex flex-col gap-y-2">
                  <ValidatedTextarea vh={vh} name="reason" code={110} required value={reason}
                    label="Rejection reason"
                    onChange={(e) => setReason(e.target.value)} rows={2}
                    placeholder="Why are you rejecting this?" data-testid="INP_wf_modal_reason" />
                  <div className="flex gap-2 justify-end">
                    <Button variant="inactive-outline" disabled={busy} onClick={() => setMode('idle')}>Cancel</Button>
                    <Button variant="danger" icon={X} disabled={busy || !reason.trim()}
                      onClick={() => {
                        if (!vh.validateAll([{ name: 'reason', code: 110, value: reason, required: true }])) return;
                        run(() => workflowService.reject(kind!, id!, reason.trim()));
                      }}
                      data-testid="BTN_wf_modal_reject_confirm">Confirm Reject</Button>
                  </div>
                </div>
              )}

              {mode === 'idle'
                && !(state === 'draft' && perms.submit)
                && !(state === 'submitted' && (perms.approve || perms.reject))
                && !((state === 'approved' || state === 'rejected') && perms.reopen) && (
                <span className="text-xs italic text-slate-400">
                  {tl?.is_active === 0
                    ? 'This record is inactive. Reactivate it before changing its workflow.'
                    : 'No action available to you at this stage.'}
                </span>
              )}
            </div>
          )}

          {/* ── Vertical timeline (latest first) ─────────────────────── */}
          {!loading && tl && (
            <div className="flex flex-col">
              {tl.events.map((e, i) => {
                // Fallback so an unrecognised action never crashes the timeline.
                const meta = ACTION_META[e.action] ?? { color: '#9ca3af', label: String(e.action || 'Event') };
                const last = i === tl.events.length - 1;
                return (
                  <div key={`${e.action}-${i}`} style={{ display: 'grid', gridTemplateColumns: '20px 1fr', columnGap: 12 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <span style={{ width: 12, height: 12, borderRadius: '50%', background: meta.color, marginTop: 4, boxShadow: `0 0 0 3px ${meta.color}22` }} />
                      {!last && <span style={{ flex: 1, width: 2, background: '#e5e7eb', marginTop: 2, minHeight: 24 }} />}
                    </div>
                    <div style={{ paddingBottom: 16 }}>
                      <div className="flex items-baseline gap-2">
                        <span className="text-sm font-semibold" style={{ color: meta.color }}>{meta.label}</span>
                        <span className="text-sm text-slate-700">by {e.by_name}</span>
                      </div>
                      <div className="text-xs text-slate-400">{formatDateTime(e.on)}</div>
                      {e.note && <div className="text-xs text-slate-600 italic mt-1">“{e.note}”</div>}
                    </div>
                  </div>
                );
              })}
              {tl.events.length === 0 && (
                <span className="text-xs italic text-slate-400">No workflow history yet.</span>
              )}
            </div>
          )}
        </div>
      </Modal.Body>
      <Toast toast={toast} onClose={() => setToast(null)} />
    </Modal>
  );
};

export default WorkflowModal;
